/**
 * Small 16px line icons used inside the Sign In / Sign Up form fields.
 * Decorative only — every consumer renders them inside an aria-hidden slot.
 * They inherit colour via `currentColor`.
 */

const base = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
} as const;

export const UserFieldIcon = () => (
    <svg {...base}>
        <circle cx="12" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.8 19.5c0-3.3 3.2-5.4 7.2-5.4s7.2 2.1 7.2 5.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const MailFieldIcon = () => (
    <svg {...base}>
        <rect x="3" y="5.5" width="18" height="13" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4 8l8 5.4L20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const LockFieldIcon = () => (
    <svg {...base}>
        <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.2 10.5V7.8a3.8 3.8 0 017.6 0v2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const IdFieldIcon = () => (
    <svg {...base}>
        <rect x="3" y="5.5" width="18" height="13" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="9" cy="11.4" r="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M6 16c.6-1.4 1.7-2 3-2s2.4.6 3 2M14.5 10.5h3.2M14.5 13.6h3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

export const CalendarFieldIcon = () => (
    <svg {...base}>
        <rect x="3.5" y="5.5" width="17" height="14" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3.5 10h17M8.5 3.8v3.2M15.5 3.8v3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const GlobeFieldIcon = () => (
    <svg {...base}>
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3.8 12h16.4M12 3.8c2.2 2.3 3.3 5 3.3 8.2s-1.1 5.9-3.3 8.2c-2.2-2.3-3.3-5-3.3-8.2S9.8 6.1 12 3.8z" stroke="currentColor" strokeWidth="1.6" />
    </svg>
);

export const PhoneFieldIcon = () => (
    <svg {...base}>
        <path d="M7.4 4.2h-.9A2.6 2.6 0 004 6.8c0 6.3 5.1 11.4 11.4 11.4a2.6 2.6 0 002.6-2.6v-.9l-3.4-1.4-1.6 1.8a12.4 12.4 0 01-5.1-5.1l1.8-1.6L7.4 4.2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);

export const ProfessionFieldIcon = () => (
    <svg {...base}>
        <rect x="3.2" y="7.6" width="17.6" height="11.6" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.8 7.6V6.2a2 2 0 012-2h2.4a2 2 0 012 2v1.4M3.2 12.4h17.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const AddressFieldIcon = () => (
    <svg {...base}>
        <path d="M4 10.4L12 4l8 6.4V19a1.4 1.4 0 01-1.4 1.4H5.4A1.4 1.4 0 014 19v-8.6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9.6 20.4v-6h4.8v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const PostalFieldIcon = () => (
    <svg {...base}>
        <rect x="3.2" y="6" width="17.6" height="12" rx="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M7.4 10.2h3M7.4 13.8h6.4M16.6 9.4v5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const CityFieldIcon = () => (
    <svg {...base}>
        <path d="M12 21c4-4.2 6-7.2 6-9.9A6 6 0 006 11.1c0 2.7 2 5.7 6 9.9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx="12" cy="10.8" r="2.2" stroke="currentColor" strokeWidth="1.7" />
    </svg>
);

/** Green tick shown at the trailing edge of a field once its value is valid. */
export const FieldValidIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.5 12.8l4.6 4.6L19.5 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** Arrow used on the primary CTA. */
export const CtaArrowIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M5 12h13M13 6.5l5.5 5.5L13 17.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
